import { useCallback, useRef, useState } from 'react';

/**
 * How long to hold a Bluetooth link open after a scan, and how often to poke
 * it. Four seconds is comfortably inside the idle window of every adapter
 * tested and cheap enough that the traffic is invisible.
 */
const HOLD_MS = 15_000;
// One second, so the countdown means seconds and the gap between pings is
// short enough for an adapter that hangs up the moment traffic stops.
const HOLD_TICK_MS = 1_000;
import { AdapterGoneError, Elm327, allDtcs, type LogEntry, type ScanProgress, type ScanResult } from './elm327.ts';
import { PIDS } from './obd/pids.ts';
import { findFordOdometer, sweepModules, SWEEP_MODULES, type Mode22Probe, type ModulePresence } from './obd/mode22.ts';
import type { Transport, TransportStatus } from './transports/types.ts';
import { WifiTransport } from './transports/wifi.ts';
import { SimulatorTransport } from './transports/simulator.ts';
import { SerialTransport } from './transports/webserial.ts';
import { BluetoothTransport } from './transports/webbluetooth.ts';
import { api, type Adapter } from './api.ts';

// Owns the live connection. The transport and the ELM327 instance are refs
// rather than state, because replacing them should never trigger a render -
// only the status they report should.

export interface ScannerState {
  status: TransportStatus;
  busy: boolean;
  progress: ScanProgress | null;
  error: string | null;
  result: ScanResult | null;
  savedScanId: number | null;
  log: LogEntry[];
}

export function buildTransport(adapter: Adapter, simScenario?: string): Transport {
  switch (adapter.transport) {
    case 'wifi':
      return new WifiTransport({ host: adapter.host ?? undefined, port: adapter.port ?? undefined });
    case 'sim':
      return new SimulatorTransport(simScenario);
    case 'serial':
      return new SerialTransport();
    case 'ble':
      return new BluetoothTransport();
  }
}

/** How many samples each sparkline keeps. Enough to see a trend, not a history. */
export const LIVE_WINDOW = 90;

/** Streaming fewer PIDs makes each one refresh faster, so the default is a useful four. */
export const DEFAULT_LIVE_KEYS = ['rpm', 'speedKph', 'coolantC', 'loadPct'];

export function useScanner() {
  const [status, setStatus] = useState<TransportStatus>({ state: 'idle' });
  /** Seconds the connection is being actively held open after a scan. */
  const [holdSecondsLeft, setHoldSecondsLeft] = useState(0);
  const holdTimerRef = useRef<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<ScanProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [savedScanId, setSavedScanId] = useState<number | null>(null);
  const [log, setLog] = useState<LogEntry[]>([]);

  const [live, setLive] = useState(false);
  const [liveKeys, setLiveKeys] = useState<string[]>(DEFAULT_LIVE_KEYS);
  const [samples, setSamples] = useState<Record<string, number[]>>({});
  const [liveHz, setLiveHz] = useState<number | null>(null);

  const transportRef = useRef<Transport | null>(null);
  const elmRef = useRef<Elm327 | null>(null);
  /** What the last scan connected to, so live data can reopen it silently. */
  const lastConnectionRef = useRef<{ adapter: Adapter; simScenario?: string } | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);
  // A ref, not state: the loop reads it every iteration and must see the
  // current value rather than the one captured when it started.
  const liveRef = useRef(false);
  const liveKeysRef = useRef<string[]>(DEFAULT_LIVE_KEYS);

  const stopLive = useCallback(() => {
    liveRef.current = false;
  }, []);

  /**
   * Keep the link alive for a moment after a scan.
   *
   * Nothing here closes the connection when a scan ends - that is deliberate,
   * so live data and clearing codes can reuse it. A Bluetooth adapter does not
   * agree: left with no traffic it drops the GATT link on its own, and the
   * first thing the user sees is the transport reporting 'disconnected'.
   *
   * So the fix is traffic, not a longer grace period. ATI asks the adapter to
   * identify itself and never reaches the vehicle bus, which makes it a safe
   * heartbeat. It stops as soon as streaming starts, because streaming is
   * itself traffic, and after HOLD_MS if nobody does anything.
   */
  const cancelHold = useCallback(() => {
    if (holdTimerRef.current !== null) {
      clearInterval(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    setHoldSecondsLeft(0);
  }, []);

  const beginHold = useCallback(() => {
    cancelHold();
    let remaining = Math.round(HOLD_MS / 1000);
    setHoldSecondsLeft(remaining);

    // Failure means the adapter has already gone. Its own status event reports
    // that, so there is nothing to add here.
    const ping = () => void elmRef.current?.command('ATI').catch(() => undefined);

    // Immediately, not on the first tick. An adapter that hangs up as soon as
    // the line goes quiet is gone before a delayed first ping arrives, which
    // is the whole case this exists for.
    ping();

    holdTimerRef.current = window.setInterval(() => {
      remaining -= 1;
      setHoldSecondsLeft(remaining);

      if (remaining <= 0) {
        cancelHold();
        return;
      }
      ping();
    }, HOLD_TICK_MS);
  }, [cancelHold]);

  const teardown = useCallback(async () => {
    cancelHold();
    liveRef.current = false;
    unsubRef.current?.();
    unsubRef.current = null;
    try {
      await elmRef.current?.disconnect();
    } catch {
      // closing a dead connection is not an error worth surfacing
    }
    elmRef.current = null;
    transportRef.current = null;
  }, [cancelHold]);

  const disconnect = useCallback(async () => {
    await teardown();
    setStatus({ state: 'closed' });
    setProgress(null);
  }, [teardown]);

  /** Connect, run every read, and persist the result in one go. */
  const scan = useCallback(
    async (adapter: Adapter, simScenario?: string) => {
      setBusy(true);
      setError(null);
      setResult(null);
      setSavedScanId(null);
      setLog([]);
      setProgress({ step: 'Starting', done: 0, total: 5 });

      // A scan replaces the connection, so any running stream ends with it.
      liveRef.current = false;
      setLive(false);
      setSamples({});
      cancelHold();
      await teardown();

      lastConnectionRef.current = { adapter, simScenario };

      try {
        const transport = buildTransport(adapter, simScenario);
        transportRef.current = transport;
        unsubRef.current = transport.onStatus(setStatus);

        const elm = new Elm327(transport, {
          onLog: (entry) => setLog((prev) => [...prev.slice(-400), entry]),
        });
        elmRef.current = elm;

        await transport.open();
        const scanResult = await elm.runFullScan(setProgress);
        setResult(scanResult);

        // Persist. A failure here must not look like a failed scan, because
        // the reading itself succeeded and is still on screen.
        try {
          const saved = await api.saveScan({
            vin: scanResult.vin,
            adapterId: adapter.id,
            transport: adapter.transport,
            protocol: scanResult.protocol,
            milOn: scanResult.milOn,
            vitals: scanResult.vitals,
            freezeFrame: scanResult.freezeFrame,
            readiness: scanResult.readiness,
            monitorTests: scanResult.monitorTests,
            vehicleInfo: scanResult.vehicleInfo,
            supportedPids: scanResult.supportedPids,
            adapterVoltage: scanResult.adapterVoltage,
            // Read from the vehicle rather than typed, so it is marked as such.
            // A hand-entered reading should never be overwritten by a probe.
            odometerKm: scanResult.odometerKm,
            odometerSource: scanResult.odometerKm != null ? 'obd' : null,
            rawLog: scanResult.rawLog,
            dtcs: allDtcs(scanResult).map((d) => ({
              code: d.code,
              kind: d.kind,
              description: d.description,
              system: d.system,
              severity: d.severity,
            })),
          });
          setSavedScanId(saved.id);
        } catch (err) {
          setError(`Scan completed but was not saved: ${(err as Error).message}`);
        }

        return scanResult;
      } catch (err) {
        if (err instanceof AdapterGoneError) {
          // Distinguish this from a scan that never started. Everything read
          // before the adapter went is real; what is missing is unknown rather
          // than absent, and presenting the partial result as a finished scan
          // is how a car looks clean because the link died early.
          setError(
            'The adapter disconnected part-way through, so this scan is incomplete. ' +
              'Nothing read before that point is wrong, but anything missing is unknown rather than absent. ' +
              'Check the Adapter log for where it stopped, then scan again.',
          );
        } else {
          setError((err as Error).message);
        }
        return null;
      } finally {
        setBusy(false);
        setProgress(null);
        // Only worth holding if the connection actually survived the scan.
        if (elmRef.current) beginHold();
      }
    },
    [teardown, beginHold],
  );

  /**
   * Poll the selected PIDs until stopped.
   *
   * The adapter is a single-threaded device and the command queue serialises
   * everything, so this loop cannot interleave with a scan. It reuses the open
   * connection from the last scan rather than reconnecting, which is why the
   * transport is deliberately left open when a scan finishes.
   */
  const startLive = useCallback(async () => {
    // The adapter drops an idle BLE link on its own, so by the time someone
    // presses this the connection from the scan has often gone. Reopening is
    // silent: Web Bluetooth remembers the granted device, so there is no
    // chooser and no need for the adapter to be advertising for a prompt.
    // Whether the link is alive, not whether the object exists. A dropped
    // adapter leaves elmRef pointing at a perfectly good Elm327 wrapped around
    // a closed transport, so checking for the object found nothing wrong and
    // streaming went ahead against a dead link.
    const linkAlive = Boolean(elmRef.current && transportRef.current?.isOpen);

    if (!linkAlive && lastConnectionRef.current) {
      // Clear the stale pair first, or the old transport's status events keep
      // arriving and overwrite the new connection's.
      await teardown();
      const { adapter, simScenario } = lastConnectionRef.current;
      try {
        const transport = buildTransport(adapter, simScenario);
        transportRef.current = transport;
        unsubRef.current = transport.onStatus(setStatus);
        const reopened = new Elm327(transport, {
          onLog: (entry) => setLog((prev) => [...prev.slice(-400), entry]),
        });
        await transport.open();
        await reopened.connect();
        elmRef.current = reopened;
      } catch (err) {
        await teardown();
        setError(`Could not reopen the connection for live data: ${(err as Error).message}`);
        return;
      }
    }

    const elm = elmRef.current;
    if (!elm) {
      setError('Run a scan first. Live data reuses that connection, or reopens it if the adapter has dropped.');
      return;
    }
    if (liveRef.current) return;
    // Streaming is traffic in its own right, so the heartbeat is redundant.
    cancelHold();

    liveRef.current = true;
    setLive(true);
    setSamples({});
    setLiveHz(null);

    let consecutiveFailures = 0;
    let sweeps = 0;
    let windowStart = Date.now();

    while (liveRef.current) {
      const specs = PIDS.filter((p) => liveKeysRef.current.includes(p.key));
      if (specs.length === 0) break;

      try {
        const sweep: Record<string, number | null> = {};
        for (const spec of specs) {
          if (!liveRef.current) break;
          sweep[spec.key] = await elm.readVital(spec);
        }
        if (!liveRef.current) break;

        setSamples((prev) => {
          const next: Record<string, number[]> = { ...prev };
          for (const [key, value] of Object.entries(sweep)) {
            if (value === null) continue;
            const series = [...(next[key] ?? []), value];
            next[key] = series.length > LIVE_WINDOW ? series.slice(-LIVE_WINDOW) : series;
          }
          return next;
        });
        consecutiveFailures = 0;
      } catch (err) {
        // A lost adapter will not recover on the next sweep, so retrying twice
        // more just delays the message by a couple of seconds.
        if (err instanceof AdapterGoneError) {
          setError('The adapter disconnected, so live data stopped. Scan again to reconnect.');
          break;
        }
        consecutiveFailures++;
        if (consecutiveFailures >= 3) {
          setError(`Live data stopped: ${(err as Error).message}`);
          break;
        }
      }

      sweeps++;
      const elapsed = Date.now() - windowStart;
      if (elapsed >= 1_000) {
        setLiveHz(Number((sweeps / (elapsed / 1_000)).toFixed(1)));
        sweeps = 0;
        windowStart = Date.now();
      }
    }

    liveRef.current = false;
    setLive(false);
  }, [teardown, cancelHold]);

  const toggleLiveKey = useCallback((key: string) => {
    setLiveKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      // The running loop reads the ref, so it picks this up on the next sweep
      // without needing a restart.
      liveKeysRef.current = next;
      return next;
    });
  }, []);

  const clearCodes = useCallback(async () => {
    const elm = elmRef.current;
    if (!elm) {
      setError('Not connected. Run a scan first, then clear without disconnecting.');
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      await elm.clearCodes();
      return true;
    } catch (err) {
      setError((err as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const state: ScannerState = { status, busy, progress, error, result, savedScanId, log };
  /**
   * Ask the vehicle's modules for an odometer, and report what came back.
   *
   * Deliberately reports rather than decides. PID 0xA6 is almost never
   * supported and the identifiers below are candidates, not facts, so this
   * returns raw bytes with every plausible scaling beside them and leaves the
   * judgement to somebody who can see the dashboard.
   *
   * That comparison is the whole point: a number that looks like mileage is
   * easy to produce and worthless unless it matches the instrument cluster.
   */
  async function probeOdometer(adapter: Adapter): Promise<Mode22Probe[]> {
    await teardown();
    const transport = buildTransport(adapter, 'clean');
    transportRef.current = transport;
    unsubRef.current = transport.onStatus(setStatus);

    const elm = new Elm327(transport, {
      onLog: (entry) => setLog((prev) => [...prev.slice(-400), entry]),
    });
    elmRef.current = elm;

    await transport.open();
    await elm.connect();
    try {
      return await findFordOdometer((cmd, timeoutMs) => elm.command(cmd, timeoutMs));
    } finally {
      await teardown();
    }
  }

  /** Knock on every known address and report who is home. */
  async function probeModules(adapter: Adapter): Promise<ModulePresence[]> {
    await teardown();
    const transport = buildTransport(adapter, 'clean');
    transportRef.current = transport;
    unsubRef.current = transport.onStatus(setStatus);

    const elm = new Elm327(transport, {
      onLog: (entry) => setLog((prev) => [...prev.slice(-400), entry]),
    });
    elmRef.current = elm;

    await transport.open();
    await elm.connect();
    try {
      return await sweepModules((cmd, timeoutMs) => elm.command(cmd, timeoutMs), SWEEP_MODULES);
    } finally {
      await teardown();
    }
  }

  return {
    ...state,
    scan,
    probeOdometer,
    probeModules,
    clearCodes,
    disconnect,
    setError,
    holdSecondsLeft,
    live,
    liveKeys,
    samples,
    liveHz,
    startLive,
    stopLive,
    toggleLiveKey,
  };
}
